import {
  Component,
  Event,
  EventEmitter,
  forceUpdate,
  Fragment,
  getAssetPath,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import wcShared from '@web-components/shared';

import avatarsBotClass from '../../utils/avatars-bot.class';
import { SupportPremiumLevelId } from '../../utils/enum';
import helpdeskCommon from '../../utils/HelpdeskCommon.class';
import { FaqsStore } from '../../utils/Stores/faqs-store';
import { InitStore } from '../../utils/Stores/init-store';
import { MessagesStore } from '../../utils/Stores/messages-store';
import { SectionStore } from '../../utils/Stores/section-store';
import { Store } from '../../utils/Stores/store';

@Component({
  tag: 'main-helpdesk-switch-component',
  styleUrl: 'main-helpdesk-switch-module.scss',
  shadow: true,
})
export class ModuleHelpdeskSwitch {
  @Event() sectionChangeEvent: EventEmitter<IWCContextClose[]>;

  @Prop() readonly faq: IWCFaq;
  @Prop() readonly contact_event: boolean;

  @State() init: IWCInit;
  @State() loadingInit = false;
  @State() loadingMessage = false;
  @State() reload = false;
  @State() loadingReset = false;

  private project = window?.['CURRENT_PROJECT'];
  private supportLevel = null;
  private currentEventsCount = 0;

  async componentWillLoad() {
    // Init routing
    Store?.set('routing', [
      {
        routing: 'landing',
        faq: null,
      },
    ]);

    history.pushState(null, null, location.href);

    window.onpopstate = () => {
      this.back();
    };

    if (Boolean(this.faq?.id)) {
      this.changeSection(['faq_item']);
    } else {
      // If no section is selected, we open the chat by default
      if (
        !Boolean(
          Object.keys(SectionStore?.get('section')).find(
            (item) => SectionStore?.get('section')[item],
          ),
        )
      ) {
        SectionStore?.set('section', {
          ...SectionStore?.get('section'),
          landing: true,
        });
      }
    }

    this.currentEventsCount = wcShared.store.currentEvents?.length ?? 0;

    this.getInit();
    this.getMessages();
    document.addEventListener('reloadApp', this.reloadApp);
    document.addEventListener('click', this.clickOutside);
    window.addEventListener('blur', () => {
      if (document.activeElement === document.querySelector('iframe')) {
        this.close();
      }
    });
  }

  componentWillRender() {
    helpdeskCommon.hasScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('reloadApp', this.reloadApp);
    document.removeEventListener('click', this.clickOutside);
  }

  private clickOutside = (event) => {
    const isClickInside = document
      ?.querySelector('module-helpdesk-component')
      ?.contains(event.target);

    if (!isClickInside) {
      this.close();
    }
  };

  @Watch('faq')
  watchFaq(newValue: IWCFaq) {
    if (Boolean(newValue?.id)) {
      this.changeSection(['faq_item']);
    }
  }

  @Listen('getInitEvent')
  getInitEvent() {
    this.getInit();
  }

  // Listener used to call back in history
  @Listen('backEvent')
  backEventHandler() {
    this.back();
  }

  // Close
  @Listen('closeEvent')
  closeEventHandler() {
    this.close();
  }

  // Listener used to call back in history
  @Listen('sectionChangeEvent')
  sectionChangeEventHandler(event: CustomEvent<IWCContextClose[] | IWCSectionVisibility>) {
    if (Array.isArray(event.detail)) {
      this.changeSection(event.detail);
    } else {
      SectionStore?.set('section', event.detail);
      this.reload = !this.reload;
    }
  }

  // Listener used to call back in history and replace last entry
  @Listen('sectionReplaceEvent')
  sectionReplaceEventHandler(event: CustomEvent<IWCContextClose[] | IWCSectionVisibility>) {
    if (Array.isArray(event.detail)) {
      this.changeSection(event.detail, true);
    } else {
      SectionStore?.set('section', event.detail);
      this.reload = !this.reload;
    }
  }

  private changeSection(value: IWCContextClose[], shouldReplace = false) {
    // Necessary to have data of FaqStore.current
    setTimeout(() => {
      // Check that not an event
      if (!['error_unauthorized', 'error_default', 'alert_continue'].includes(value[0])) {
        // For FAQ
        if (value[0] === 'faq_item') {
          // Check that not the same FAQ
          if (
            Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.faq?.id !==
            FaqsStore?.get('current')?.id
          ) {
            if (shouldReplace) {
              history.replaceState(null, null, location.href);
            } else {
              history.pushState(null, null, location.href);
            }

            // Add routing
            let historyRouting: unknown[] = Store?.get('routing');

            if (shouldReplace) {
              historyRouting = historyRouting.slice(0, historyRouting.length - 1); // remove last entry
            }

            Store?.set('routing', [
              ...historyRouting,
              {
                routing: value[0],
                faq: FaqsStore?.get('current'),
              },
            ]);
          }
        }
        // For else
        else {
          // Check that not the same section
          if (Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.routing !== value[0]) {
            if (shouldReplace) {
              history.replaceState(null, null, location.href);
            } else {
              history.pushState(null, null, location.href);
            }

            // Add routing
            let historyRouting: unknown[] = Store?.get('routing');

            if (shouldReplace) {
              historyRouting = historyRouting.slice(0, historyRouting.length - 1); // remove last entry
            }

            Store?.set('routing', [
              ...historyRouting,
              {
                routing: value[0],
                faq: null,
              },
            ]);
          }
        }
      }

      Object.keys(SectionStore?.get('section')).map((item: IWCContextClose) => {
        SectionStore?.set('section', {
          ...SectionStore?.get('section'),
          [item]: value.includes(item),
        });
      });
      this.reload = !this.reload;
    });
  }

  private reloadApp = () => {
    forceUpdate(this);
  };

  private getInit() {
    if (this.loadingInit) {
      return;
    }

    this.loadingInit = true;
    wcShared?.store?.loaderStart('loadingInit');

    let url = `/api/components/chatbot/init?lang=${wcShared?.store?.getLang()}&with=*`;
    url += helpdeskCommon.urlFormatFaq();

    return wcShared
      .httpCall('GET', url)
      .then((r) => {
        if (r.status === 200) {
          return r.json().then((response) => {
            InitStore.set('init', {
              ...InitStore?.get('init'),
              ...response.data,
            });

            const currentGroup: IWCInitAccount = InitStore?.get('init')?.accounts.find(
              (item) => item.id === InitStore?.get('init')?.user?.account?.id,
            );
            if (Boolean(currentGroup)) {
              Store?.set(
                'isInPartnership',
                currentGroup.in_partnership_full_access || currentGroup.in_partnership_limited,
              );
              Store?.set('supportPremium', {
                id: Boolean(currentGroup.support_premium)
                  ? currentGroup?.support_premium?.id
                  : null,
                supportLevel: Boolean(currentGroup.support_premium)
                  ? currentGroup?.support_level
                  : SupportPremiumLevelId.included,
                vipCode: Boolean(currentGroup.support_premium)
                  ? currentGroup?.support_premium?.vip_code
                  : null,
              });
              Store?.set('currentGroup', currentGroup);
              this.supportLevel = Store?.get('supportPremium').supportLevel;
            }

            this.checkEventStatus();

            // Get url of user Avatar or generic image url
            avatarsBotClass.initAvatar();
            wcShared?.store?.loaderStop('loadingInit').then(() => (this.loadingInit = false));
          });
        }
      })
      .catch(() => {
        wcShared?.store?.loaderStop('loadingInit').then(() => (this.loadingInit = false));
      });
  }

  private getMessages() {
    if (this.loadingMessage) {
      return;
    }

    this.loadingMessage = true;
    wcShared?.store?.loaderStart('loadingMessage');

    let url = `/api/components/chatbot/conversation?lang=${wcShared?.store?.getLang()}&timezone=${
      Boolean(Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone)
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'Europe/Zurich'
    }`;
    url += helpdeskCommon.urlFormatFaq();

    return wcShared
      .httpCall('GET', url)
      .then((r) => {
        wcShared?.store?.loaderStop('loadingMessage').then(() => (this.loadingMessage = false));
        if (r.status === 200) {
          Store?.set('onError', false);
          return r.json().then((response) => {
            Store?.set('messageData', {
              ...Store?.get('messageData'),
              conversation_uuid: response.data?.channel_id,
            });
            Store?.set('bot_id', response.data.bot_id);
            Store?.set('channel_id', response.data.channel_id);

            this.checkEventStatus();

            // Delete empty message
            const res = response.data.messages.filter((item) => {
              if (item.type === 'text' && item.title === '') {
                return;
              }
              return item;
            });

            MessagesStore?.set('messages', res);

            forceUpdate(this);
          });
        } else if (r.status === 401) {
          Store?.set('onError', true);
          this.sectionChangeEvent.emit(['error_unauthorized']);
        } else {
          Store?.set('onError', true);
          this.sectionChangeEvent.emit(['error_default']);
        }
      })
      .catch((err) => {
        console.error(err);
        wcShared?.store?.loaderStop('loadingMessage').then(() => (this.loadingMessage = false));
      });
  }

  private close() {
    if (Store?.get('is_full')) {
      return;
    }

    if (SectionStore?.get('section')?.contact_mail) {
      if (!Store?.get('displayModal')) {
        Store?.set('displayModal', true);
        Store?.set('typeModal', 'close');
        return;
      } else {
        Store?.set('displayModal', false);
        Store?.set('typeModal', null);
      }
    }

    if (
      (SectionStore?.get('section')?.faq_item || SectionStore?.get('section')?.faq_list) &&
      !Store?.get('isOpenFaqTrigger')
    ) {
      this.changeSection(['landing']);
    } else {
      Store?.set('isOpenFaqTrigger', false);

      // Notify `main-helpdesk`to close this view
      wcShared?.store?.notifyObservers('displayHelpdesk', false);

      Object.keys(SectionStore?.get('section')).map((item) => {
        SectionStore?.set('section', {
          ...SectionStore?.get('section'),
          [item]: false,
        });
      });

      // Delete cookie use to open the chatbot
      wcShared?.store?.utils?.setCookie('IkHelpdeskOpen', 'false', 365);
    }
    document.dispatchEvent(new CustomEvent('openSupportModuleFaq', { detail: null }));
  }

  private checkEventStatus() {
    // ERROR : DEFAULT
    if (SectionStore?.get('section')?.error_default) {
      this.changeSection(['error_default']);
    }

    // ERROR : 401 unauthorized
    else if (SectionStore?.get('section')?.error_unauthorized) {
      this.changeSection(['error_unauthorized']);
    }

    // ALERT : CONTINUE
    else if (
      InitStore?.get('init')?.has_conversation &&
      !Store?.get('is_active') &&
      !InitStore?.get('init')?.continue_event &&
      MessagesStore?.get('messages').length > 1 &&
      SectionStore?.get('section')?.landing &&
      !Store?.get('is_report_abuse')
    ) {
      this.changeSection(['alert_continue']);
    }
  }

  private checkIfIs(type: 'event' | 'contact') {
    switch (type) {
      case 'event':
        return (
          SectionStore?.get('section')?.error_default ||
          SectionStore?.get('section')?.error_unauthorized ||
          SectionStore?.get('section')?.alert_continue
        );
      case 'contact':
        return (
          SectionStore?.get('section')?.contact_list ||
          SectionStore?.get('section')?.contact_chat ||
          SectionStore?.get('section')?.contact_phone ||
          SectionStore?.get('section')?.contact_callmeback ||
          SectionStore?.get('section')?.contact_mail ||
          SectionStore?.get('section')?.contact_mail_jelastic ||
          SectionStore?.get('section')?.contact_mail_success ||
          SectionStore?.get('section')?.contact_mail_error
        );
    }
  }

  private back = async () => {
    this.loadingInit = true;
    if (Store?.get('routing')?.length > 0) {
      const routing = Store?.get('routing');
      routing.pop();
      Store?.set('routing', routing);
    }

    if (SectionStore?.get('section')?.contact_list) {
      await this.resetConversation().then(() => (this.loadingInit = false));
    }

    // Use to have history of faq
    if (Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.faq) {
      FaqsStore?.set('current', Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.faq);
      this.changeSection([Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.routing]);
    } else {
      this.changeSection([Store?.get('routing')?.[Store?.get('routing')?.length - 1]?.routing]);
    }
    this.loadingInit = false;
    this.reloadApp();
  };

  private resetConversation() {
    if (this.loadingReset) {
      return;
    }

    this.loadingReset = true;

    let url = `/api/components/chatbot/reset?lang=${wcShared?.store?.getLang()}`;
    url += helpdeskCommon.urlFormatFaq();

    return wcShared
      .httpCall('GET', url)
      .then((r) => {
        if (r.status === 200) {
          return r.json().then((response) => {
            Store?.set('bot_id', response.data.bot_id);
            Store?.set('channel_id', response.data.channel_id);
            this.sectionChangeEvent.emit(['landing']);

            this.loadingReset = false;

            MessagesStore?.set('messages', response?.data?.messages);
            Store?.set('is_active', true);
            InitStore.set('init', {
              ...InitStore.get('init'),
              continue_event: true,
            });
          });
        }
        return;
      })
      .catch((err) => {
        console.error(err);
        return (this.loadingReset = false);
      });
  }

  private tradIncident() {
    if (this.currentEventsCount > 1) {
      return wcShared?.translate('$number$ incidents sont en cours', {
        number: this.currentEventsCount,
      });
    } else {
      return wcShared?.translate('1 incident est en cours');
    }
  }

  private goToStatus() {
    window.open('https://infomaniakstatus.com', '_blank');
  }

  private isEvent() {
    return (
      SectionStore?.get('section')?.alert_continue ||
      SectionStore?.get('section')?.error_unauthorized
    );
  }

  private modalReset() {
    Store?.set('displayModal', true);
    Store?.set('typeModal', 'reset');
  }

  private getLayoutContent() {
    const ret = [];

    if (Store?.get('displayModal') && !this.loadingInit)
      ret.push(<modals-switch-module></modals-switch-module>);

    if (this.checkIfIs('contact') && !this.loadingInit)
      ret.push(
        <contact-module
          style={{
            overflow: Store?.get('visibilityModal') ? 'hidden' : 'visible',
          }}
          reload={this.reload}
        >
          <div slot="recaptcha-slot-3">
            <slot name="recaptcha-slot-2" />
          </div>
        </contact-module>,
      );

    if (SectionStore?.get('section')?.faq_list && !this.loadingInit)
      ret.push(<faq-list-module></faq-list-module>);

    if (SectionStore?.get('section')?.faq_item && !this.loadingInit)
      ret.push(<faq-item-module faq={FaqsStore?.get('current')}></faq-item-module>);

    if (SectionStore?.get('section')?.chat && !this.loadingInit)
      ret.push(<bot-component></bot-component>);

    if (SectionStore?.get('section')?.tickets && !this.loadingInit)
      ret.push(
        <tickets-module-component
          tickets={InitStore?.get('init')?.tickets}
        ></tickets-module-component>,
      );

    if (SectionStore?.get('section')?.ticket_details && !this.loadingInit)
      ret.push(
        <ticket-details-module-component
          tickets={InitStore?.get('init')?.tickets}
          currentTicketId={Store?.get('currentTicketId')}
          goBack={this.back}
        ></ticket-details-module-component>,
      );

    if (SectionStore?.get('section')?.ticket_close && !this.loadingInit)
      ret.push(
        <ticket-close-module-component
          tickets={InitStore?.get('init')?.tickets}
          currentTicketId={Store?.get('currentTicketId')}
          goBack={this.back}
        ></ticket-close-module-component>,
      );

    if (SectionStore?.get('section')?.technical_emergency && !this.loadingInit)
      ret.push(<technical-emergency></technical-emergency>);

    if (SectionStore?.get('section')?.contact_reception && !this.loadingInit)
      ret.push(<contact-reception></contact-reception>);

    if (this.checkIfIs('event') && !this.loadingInit)
      ret.push(<events-module reload={this.reload}></events-module>);

    if (ret.length === 0) {
      return null;
    }

    return ret.map((c) => <Fragment>{c}</Fragment>);
  }

  render() {
    const withBlueBackground =
      SectionStore?.get('section')?.tickets ||
      SectionStore?.get('section')?.ticket_details ||
      SectionStore?.get('section')?.ticket_close;

    if (this.loadingInit || this.loadingMessage) {
      return <module-loader-component />;
    } else {
      const layoutContent = this.getLayoutContent();
      return (
        <Host class={{ 'blue-background': withBlueBackground }}>
          {SectionStore?.get('section')?.landing || !layoutContent ? (
            <landing-module-component
              first-name={InitStore?.get('init')?.user?.first_name}
              chatbotEnabled={InitStore?.get('init')?.chatbot_enabled}
            >
              <div slot="recaptcha-slot-3">
                <slot name="recaptcha-slot-2" />
              </div>
            </landing-module-component>
          ) : (
            <div class="overflow--hidden">
              {Store?.get('is_full') ? (
                <div
                  class={{
                    'header-full': true,
                    'header-full--scroll': Store?.get('hasScroll') && Store?.get('is_full'),
                  }}
                >
                  <div
                    class={{
                      'header-full-container': true,
                      blur: this.isEvent(),
                    }}
                  >
                    <div
                      class={{
                        'header-full-container__back': true,
                        hide: Store?.get('routing')?.length <= 1,
                      }}
                      onClick={() => this.back()}
                    >
                      <img
                        src={getAssetPath('./../assets/icons/arrow-left.svg')}
                        alt={wcShared?.translate('Retour')}
                      />
                    </div>
                    <div class="header-full-container__title">
                      {wcShared?.translate('Support infomaniak')}
                    </div>
                    <div class="header-incident-reset">
                      <div
                        class={{
                          'header-incident': true,
                          'header-incident--full': Store?.get('is_full'),
                          hide:
                            this.currentEventsCount === 0 ||
                            SectionStore?.get('section')?.alert_continue ||
                            SectionStore?.get('section')?.error_default ||
                            SectionStore?.get('section')?.error_unauthorized,
                        }}
                        onClick={() => this.goToStatus()}
                      >
                        <div class="header-incident__icon">
                          <img
                            src={getAssetPath('./../assets/icons/warning.svg')}
                            alt={wcShared?.translate('Attention')}
                          />
                        </div>
                        <div class="header-incident__text">
                          <span class="desktop">{this.tradIncident()}</span>
                          <span class="mobile">{this.currentEventsCount}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      class={{
                        'header-new-question': true,
                        hide:
                          !Store?.get('is_full') ||
                          MessagesStore?.get('messages').length <= 1 ||
                          SectionStore?.get('section')?.faq_item ||
                          SectionStore?.get('section')?.faq_list ||
                          SectionStore?.get('section')?.tickets ||
                          SectionStore?.get('section')?.ticket_details ||
                          SectionStore?.get('section')?.ticket_close,
                      }}
                      onClick={() => this.resetConversation()}
                    >
                      <div class="header-new-question__icon">
                        <img
                          src={getAssetPath('./../assets/icons/random-password.svg')}
                          alt={wcShared?.translate('Question')}
                        />
                      </div>
                      {/* <div class="header-new-question__text">
                          {wcShared?.translate('Nouvelle question')}
                        </div> */}
                    </div>
                  </div>
                  <div class={{ 'header-full-bg': true, display: this.isEvent() }}></div>
                </div>
              ) : (
                <div
                  class={{
                    header: true,
                    'background-pro': this.supportLevel === SupportPremiumLevelId.pro,
                    'background-business': this.supportLevel === SupportPremiumLevelId.business,
                    'background-transparent': withBlueBackground,
                  }}
                  data-e2e="wc-helpdesk-header"
                >
                  <div
                    class={{
                      'header-incident': true,
                      hide:
                        this.currentEventsCount === 0 ||
                        SectionStore?.get('section')?.alert_continue ||
                        SectionStore?.get('section')?.error_default ||
                        SectionStore?.get('section')?.error_unauthorized,
                      'header-incident--no-back': Store?.get('routing')?.length <= 1,
                    }}
                    data-e2e="wc-helpdesk-header__incident"
                    onClick={() => this.goToStatus()}
                  >
                    <div class="header-incident__icon">
                      <img
                        src={getAssetPath('./../assets/icons/warning.svg')}
                        alt={wcShared?.translate('Attention')}
                      />
                    </div>
                    <div class="header-incident__text">
                      <span class="desktop">{this.tradIncident()}</span>
                      <span class="mobile">{this.currentEventsCount}</span>
                    </div>
                  </div>
                  <div
                    class="header__user"
                    data-e2e="wc-helpdesk-header__user"
                  >
                    {Boolean(InitStore?.get('init')?.user?.id) ? (
                      <module-avatar-component
                        name={
                          InitStore?.get('init')?.user?.first_name +
                          ' ' +
                          InitStore?.get('init')?.user?.last_name
                        }
                        size={45}
                        accountId={InitStore?.get('init')?.user?.id}
                        logo={InitStore?.get('init')?.user?.avatar_url}
                      ></module-avatar-component>
                    ) : (
                      <a
                        href={
                          wcShared?.store?.productsRouting?.welcome +
                          '/auth/login?project=' +
                          this.project
                        }
                      >
                        <img
                          src={Store?.get('userAvatar')?.url}
                          alt={Store?.get('userAvatar')?.name}
                        />
                        <span>{wcShared?.translate('Se connecter')}</span>
                      </a>
                    )}
                  </div>
                  <div
                    class={{
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      header__back: true,
                      hide: Store?.get('routing')?.length <= 1,
                    }}
                    onClick={() => {
                      this.back();
                    }}
                  >
                    <img
                      src={getAssetPath('./../assets/icons/arrow-left.svg')}
                      alt={wcShared.translate('Retour')}
                    />
                  </div>
                  <div
                    class={{
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      header__refresh: true,
                      hide:
                        MessagesStore?.get('messages').length <= 1 ||
                        SectionStore?.get('section')?.faq_item ||
                        SectionStore?.get('section')?.faq_list ||
                        SectionStore?.get('section')?.tickets,
                    }}
                    onClick={() => {
                      this.modalReset();
                    }}
                  >
                    {Store?.get('isMobile') ? (
                      <img
                        src={getAssetPath('./../assets/icons/bin.svg')}
                        alt={wcShared.translate('Poubelle')}
                      />
                    ) : (
                      <span>{wcShared?.translate('Nouvelle question')}</span>
                    )}
                  </div>
                  <div
                    class={{
                      // eslint-disable-next-line @typescript-eslint/naming-convention
                      header__close: true,
                      hide: window['CHATBOT_FULL'],
                    }}
                    data-e2e="wc-helpdesk-header__close"
                    onClick={() => {
                      this.close();
                    }}
                  >
                    <img
                      src={getAssetPath('./../assets/icons/white-cross.svg')}
                      alt={wcShared.translate('Fermer')}
                    />
                  </div>
                  <div
                    class={{
                      'header--separator': true,
                      'background-pro': this.supportLevel === SupportPremiumLevelId.pro,
                      'background-business': this.supportLevel === SupportPremiumLevelId.business,
                      'background-transparent': withBlueBackground,
                    }}
                  ></div>
                </div>
              )}
              <div
                class={{
                  layout: true,
                  'layout--white':
                    !Store?.get('is_full') &&
                    (SectionStore?.get('section')?.contact_mail_error ||
                      SectionStore?.get('section')?.contact_mail_success ||
                      SectionStore?.get('section')?.error_default),
                  'layout--hidden':
                    SectionStore?.get('section')?.error_default ||
                    SectionStore?.get('section')?.error_unauthorized ||
                    Store?.get('displayModal'),
                  'layout--chat': SectionStore?.get('section')?.chat,
                  'layout--event': this.checkIfIs('event'),
                  'layout--contact': this.checkIfIs('contact'),
                  'layout--callmeback': SectionStore?.get('section')?.contact_callmeback,
                  'layout--contact-list': SectionStore?.get('section')?.contact_list,
                  'layout--contact-mail': SectionStore?.get('section')?.contact_mail,
                  'layout--contact-phone': SectionStore?.get('section')?.contact_phone,
                  'layout--contact-jelastic': SectionStore?.get('section')?.contact_mail_jelastic,
                  'layout--full': Store?.get('is_full'),
                  'layout--scroll': Store?.get('hasScroll') && Store?.get('is_full'),
                }}
              >
                {layoutContent}
              </div>
            </div>
          )}
        </Host>
      );
    }
  }
}
